/**
 * Kênh BETA — mới có giao diện chuẩn bị, backend chưa kết nối thật.
 *
 * Nguồn: khảo sát luồng "Thêm kênh" của SaleSmartly + tài liệu API chính thức
 * (docs/KET-NOI-KENH-MOI-API.md). Khi làm backend cho kênh nào thì chuyển kênh
 * đó sang CHANNELS chính (channel-catalog.tsx) với tab quản lý thật.
 */
import { i18n } from '@/lib/i18n'

export type BetaMethod = 'oauth' | 'token' | 'form' | 'qr'

export interface BetaField {
  key: string
  label: string
  placeholder?: string
  secret?: boolean
  help?: string
}

export interface BetaSpec {
  /** Tên API chính thức (hoặc "Không có API chính thức"). */
  api: string
  docsUrl?: string
  method: BetaMethod
  /** Nhãn nút kết nối chính, vd "Đăng nhập Facebook", "Quét mã QR". */
  connectLabel: string
  steps: string[]
  fields?: BetaField[]
  /** Cách nhận tin: webhook / polling / giữ phiên. */
  inbound: string
  /** Cảnh báo rủi ro (tài khoản cá nhân, vi phạm điều khoản…). */
  risk?: string
  /** Ghi chú điều kiện, chi phí. */
  notes?: string[]
}

export const BETA_SPECS = {
  'whatsapp-api': {
    api: 'WhatsApp Business Platform — Cloud API (Meta)',
    docsUrl: 'https://developers.facebook.com/docs/whatsapp/cloud-api',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.whatsapp-api.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.whatsapp-api.steps.s1'),
        i18n.t('chanEvotech:beta.whatsapp-api.steps.s2'),
        i18n.t('chanEvotech:beta.whatsapp-api.steps.s3'),
      ]
    },
    get fields() {
      return [
        { key: 'wabaId', label: 'WABA ID', get placeholder() { return i18n.t('chanEvotech:beta.whatsapp-api.fields.wabaId.placeholder') } },
        { key: 'phoneNumberId', label: 'Phone Number ID' },
        { key: 'token', label: 'System User Access Token', secret: true },
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.whatsapp-api.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.whatsapp-api.notes.n1'),
        i18n.t('chanEvotech:beta.whatsapp-api.notes.n2'),
      ]
    },
  },
  'whatsapp-app': {
    get api() { return i18n.t('chanEvotech:beta.whatsapp-app.api') },
    method: 'qr',
    get connectLabel() { return i18n.t('chanEvotech:beta.whatsapp-app.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.whatsapp-app.steps.s1'),
        i18n.t('chanEvotech:beta.whatsapp-app.steps.s2'),
        i18n.t('chanEvotech:beta.whatsapp-app.steps.s3'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.whatsapp-app.inbound') },
    get risk() { return i18n.t('chanEvotech:beta.whatsapp-app.risk') },
  },
  instagram: {
    api: 'Instagram API with Instagram Login (Meta)',
    docsUrl: 'https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.instagram.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.instagram.steps.s1'),
        i18n.t('chanEvotech:beta.instagram.steps.s2'),
        i18n.t('chanEvotech:beta.instagram.steps.s3'),
      ]
    },
    inbound: 'Webhook Meta (messages, comments)',
    get notes() {
      return [
        i18n.t('chanEvotech:beta.instagram.notes.n1'),
        i18n.t('chanEvotech:beta.instagram.notes.n2'),
      ]
    },
  },
  'facebook-comments': {
    api: 'Graph API — Page Webhooks (field feed) + Comments API',
    docsUrl: 'https://developers.facebook.com/docs/graph-api/webhooks/reference/page/',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.facebook-comments.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.facebook-comments.steps.s1'),
        i18n.t('chanEvotech:beta.facebook-comments.steps.s2'),
        i18n.t('chanEvotech:beta.facebook-comments.steps.s3'),
      ]
    },
    inbound: 'Webhook Meta field feed',
  },
  email: {
    api: 'IMAP/SMTP · Gmail API · Microsoft Graph',
    docsUrl: 'https://developers.google.com/gmail/api',
    method: 'form',
    get connectLabel() { return i18n.t('chanEvotech:beta.email.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.email.steps.s1'),
        i18n.t('chanEvotech:beta.email.steps.s2'),
        i18n.t('chanEvotech:beta.email.steps.s3'),
      ]
    },
    get fields() {
      return [
        { key: 'email', get label() { return i18n.t('chanEvotech:beta.email.fields.email.label') }, placeholder: 'sales@congty.vn' },
        { key: 'imapHost', label: 'IMAP host', placeholder: 'imap.congty.vn' },
        { key: 'imapPort', label: 'IMAP port', placeholder: '993' },
        { key: 'smtpHost', label: 'SMTP host', placeholder: 'smtp.congty.vn' },
        { key: 'smtpPort', label: 'SMTP port', placeholder: '465' },
        { key: 'password', get label() { return i18n.t('chanEvotech:beta.email.fields.password.label') }, secret: true },
      ]
    },
    inbound: 'IMAP IDLE/poll · Gmail Pub/Sub · Graph change notifications',
    get notes() {
      return [
        i18n.t('chanEvotech:beta.email.notes.n1'),
      ]
    },
  },
  line: {
    api: 'LINE Messaging API (Official Account)',
    docsUrl: 'https://developers.line.biz/en/docs/messaging-api/',
    method: 'token',
    get connectLabel() { return i18n.t('chanEvotech:beta.line.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.line.steps.s1'),
        i18n.t('chanEvotech:beta.line.steps.s2'),
        i18n.t('chanEvotech:beta.line.steps.s3'),
      ]
    },
    get fields() {
      return [
        { key: 'channelId', label: 'Channel ID' },
        { key: 'channelSecret', label: 'Channel Secret', secret: true },
        { key: 'accessToken', label: 'Channel Access Token', secret: true },
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.line.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.line.notes.n1'),
      ]
    },
  },
  'line-app': {
    get api() { return i18n.t('chanEvotech:beta.line-app.api') },
    method: 'qr',
    get connectLabel() { return i18n.t('chanEvotech:beta.line-app.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.line-app.steps.s1'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.line-app.inbound') },
    get risk() { return i18n.t('chanEvotech:beta.line-app.risk') },
  },
  'tiktok-business': {
    api: 'TikTok API for Business — Business Messaging & Comment API (Open Beta)',
    docsUrl: 'https://business-api.tiktok.com/portal/docs',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.tiktok-business.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.tiktok-business.steps.s1'),
        i18n.t('chanEvotech:beta.tiktok-business.steps.s2'),
        i18n.t('chanEvotech:beta.tiktok-business.steps.s3'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.tiktok-business.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.tiktok-business.notes.n1'),
      ]
    },
  },
  'tiktok-app': {
    get api() { return i18n.t('chanEvotech:beta.tiktok-app.api') },
    method: 'qr',
    get connectLabel() { return i18n.t('chanEvotech:beta.tiktok-app.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.tiktok-app.steps.s1'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.tiktok-app.inbound') },
  },
  youtube: {
    api: 'YouTube Data API v3 — commentThreads / comments',
    docsUrl: 'https://developers.google.com/youtube/v3/docs/commentThreads',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.youtube.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.youtube.steps.s1'),
        i18n.t('chanEvotech:beta.youtube.steps.s2'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.youtube.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.youtube.notes.n1'),
      ]
    },
  },
  x: {
    api: 'X API v2 — Direct Messages',
    docsUrl: 'https://docs.x.com/x-api/direct-messages/introduction',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.x.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.x.steps.s1'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.x.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.x.notes.n1'),
      ]
    },
  },
  wechat: {
    api: 'WeChat Official Account (公众号) API',
    docsUrl: 'https://developers.weixin.qq.com/doc/offiaccount/en/Getting_Started/Overview.html',
    method: 'token',
    get connectLabel() { return i18n.t('chanEvotech:beta.wechat.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.wechat.steps.s1'),
        i18n.t('chanEvotech:beta.wechat.steps.s2'),
        i18n.t('chanEvotech:beta.wechat.steps.s3'),
      ]
    },
    get fields() {
      return [
        { key: 'appId', label: 'AppID' },
        { key: 'appSecret', label: 'AppSecret', secret: true },
        { key: 'token', label: 'Token' },
        { key: 'aesKey', label: 'EncodingAESKey', secret: true },
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.wechat.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.wechat.notes.n1'),
      ]
    },
  },
  wecom: {
    api: 'WeCom (企业微信) Server API · 微信客服',
    docsUrl: 'https://developer.work.weixin.qq.com/document/',
    method: 'token',
    get connectLabel() { return i18n.t('chanEvotech:beta.wecom.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.wecom.steps.s1'),
        i18n.t('chanEvotech:beta.wecom.steps.s2'),
      ]
    },
    get fields() {
      return [
        { key: 'corpId', label: 'CorpID' },
        { key: 'agentId', label: 'AgentID' },
        { key: 'secret', label: 'Secret', secret: true },
        { key: 'token', label: 'Token' },
        { key: 'aesKey', label: 'EncodingAESKey', secret: true },
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.wecom.inbound') },
  },
  vk: {
    api: 'VK API — Callback API / Bots Long Poll',
    docsUrl: 'https://dev.vk.com/en/api/callback/getting-started',
    method: 'token',
    get connectLabel() { return i18n.t('chanEvotech:beta.vk.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.vk.steps.s1'),
        i18n.t('chanEvotech:beta.vk.steps.s2'),
      ]
    },
    get fields() {
      return [
        { key: 'groupId', label: 'Community ID' },
        { key: 'token', label: 'Community access token', secret: true },
        { key: 'confirmation', get label() { return i18n.t('chanEvotech:beta.vk.fields.confirmation.label') } },
        { key: 'secret', label: 'Secret key', secret: true },
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.vk.inbound') },
    get notes() {
      return [
        i18n.t('chanEvotech:beta.vk.notes.n1'),
      ]
    },
  },
  slack: {
    api: 'Slack Events API + Web API',
    docsUrl: 'https://api.slack.com/apis/events-api',
    method: 'oauth',
    get connectLabel() { return i18n.t('chanEvotech:beta.slack.connectLabel') },
    get steps() {
      return [
        i18n.t('chanEvotech:beta.slack.steps.s1'),
        i18n.t('chanEvotech:beta.slack.steps.s2'),
      ]
    },
    get inbound() { return i18n.t('chanEvotech:beta.slack.inbound') },
  },
} satisfies Record<string, BetaSpec>

export type BetaKey = keyof typeof BETA_SPECS

export const METHOD_LABEL: Record<BetaMethod, string> = {
  get oauth() { return i18n.t('chanEvotech:method.oauth') },
  get token() { return i18n.t('chanEvotech:method.token') },
  get form() { return i18n.t('chanEvotech:method.form') },
  get qr() { return i18n.t('chanEvotech:method.qr') },
}
